import {
  AddIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  EditIcon,
  TimeIcon,
  WarningTwoIcon,
} from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactSelect from 'react-select';
import { useAuth } from '../../../auth/AuthContext';
import FormattedText from '../../../components/rich-text-editor/FormattedText';
import RichTextEditor from '../../../components/rich-text-editor/RichTextEditor';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  createPracticeClosure,
  deletePracticeClosure,
  getPracticeClosures,
  updatePracticeClosure,
} from '../../../services/clousures/practiceClosureService';
import {
  PracticeClosure,
  PracticeClosureCreatePayload,
  PracticeClosureUpdatePayload,
} from '../../../types/closures/types';
import { analytics } from '../../../utils/analytics';
import { validateClosureDates } from '../../../utils/dateValidation';
import { shouldShowCard } from '../../../utils/permissionUtils';
import { formatDateInPracticeTimezone } from '../../../utils/timezoneAwareDateFormatter';

interface PracticeClosuresCardProps {
  practiceId: string;
  practiceTimezone?: string | null;  // when provided, skips internal getPracticeById fetch
  practiceState?: string | null;
  closures?: PracticeClosure[] | null;  // null = parent is fetching (skip own fetch); undefined = no prop (do own fetch)
}

interface TimeOption {
  label: string;
  value: string;
}

// Generate time options in 15-minute increments (like OfficeHoursInput)
const generateTimeOptions = (): TimeOption[] => {
  const times: TimeOption[] = [];

  // Add 12:00 AM to 11:45 AM
  for (let hour = 0; hour <= 11; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const meridiem = 'AM';
      const displayHour = hour === 0 ? 12 : hour;
      const timeString = `${displayHour}:${minute
        .toString()
        .padStart(2, '0')} ${meridiem}`;
      times.push({
        label: timeString,
        value: timeString,
      });
    }
  }

  // Add 12:00 PM to 11:45 PM
  for (let hour = 12; hour <= 23; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const meridiem = 'PM';
      const displayHour = hour === 12 ? 12 : hour - 12;
      const timeString = `${displayHour}:${minute
        .toString()
        .padStart(2, '0')} ${meridiem}`;
      times.push({
        label: timeString,
        value: timeString,
      });
    }
  }

  return times;
};

const timeOptions = generateTimeOptions();

// Date chip component for consistent date display
const DateChip: React.FC<{
  label: string;
  value: string;
  icon: React.ComponentType<{ boxSize?: number | string }>;
  variant?: 'default' | 'scheduled' | 'expired';
}> = ({ label, value, icon: IconComponent, variant = 'default' }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'scheduled':
        return {
          bg: 'blue.50',
          color: 'blue.700',
          borderColor: 'blue.200',
        };
      case 'expired':
        return {
          bg: 'gray.50',
          color: 'gray.600',
          borderColor: 'gray.200',
        };
      default:
        return {
          bg: 'white',
          color: 'gray.700',
          borderColor: 'gray.200',
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <HStack
      spacing={1}
      px={2}
      py={1}
      borderRadius="md"
      border="1px solid"
      bg={styles.bg}
      color={styles.color}
      borderColor={styles.borderColor}
      fontSize="0.85rem"
      fontWeight="500"
    >
      <IconComponent boxSize={3} />
      <Text fontSize="0.85rem" fontWeight="500">
        {label}: {value}
      </Text>
    </HStack>
  );
};

// Note preview component with fade gradient and expand functionality
const NotePreview: React.FC<{
  content: string;
  maxHeight?: string;
}> = ({ content, maxHeight = '4rem' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!content || content.trim() === '') return null;

  const hasOverflow = content.length > 400; // Overflow detection

  return (
    <Box position="relative">
      <Box
        maxHeight={isExpanded ? 'none' : maxHeight}
        overflow={isExpanded ? 'visible' : 'auto'}
        position="relative"
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e0',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a0aec0',
          },
        }}
      >
        <FormattedText
          content={content}
          fontSize="1rem"
          lineHeight="1.5"
          spacing="0.75rem"
        />

        {!isExpanded && hasOverflow && (
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height="2rem"
            background="linear-gradient(transparent, white)"
            pointerEvents="none"
            zIndex={1}
          />
        )}
      </Box>

      {hasOverflow && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          mt={2}
          fontSize="0.8rem"
          color="yellow.600"
          _hover={{ bg: 'yellow.50' }}
        >
          {isExpanded ? 'Show Less' : 'View Full Note'}
          <Icon
            as={isExpanded ? ChevronUpIcon : ChevronDownIcon}
            ml={1}
            boxSize={3}
          />
        </Button>
      )}
    </Box>
  );
};

const PracticeClosuresCard: React.FC<PracticeClosuresCardProps> = ({
  practiceId,
  practiceTimezone: timezoneProp,
  practiceState: stateProp,
  closures: closuresProp,
}) => {
  const [closures, setClosures] = useState<PracticeClosure[] | null>(
    closuresProp !== null && closuresProp !== undefined ? closuresProp : null
  );
  const [loadingClosures, setLoadingClosures] = useState(false);
  const [errorClosures, setErrorClosures] = useState<string | null>(
    null
  );
  const [isClosureModalOpen, setIsClosureModalOpen] = useState(false);
  const [closureFormData, setClosureFormData] = useState<
    PracticeClosureCreatePayload | PracticeClosureUpdatePayload
  >({
    id: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    displayDate: '',
    note: '',
  });

  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<
    string | null
  >(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedClosureId, setSelectedClosureId] = useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const hasFetched = useRef(false);
  const [practiceTimezone, setPracticeTimezone] = useState<
    string | null
  >(null);
  const [practiceState, setPracticeState] = useState<string | null>(
    null
  );
  const toast = useToast();
  const { accessToken, isAuthenticated } = useAuth();

  // Get user permissions
  const {
    canEditClosures,
    canViewDisplayDate,
    canEditDisplayDate,
    isAdmin,
  } = usePermissions();

  // Helper function to format date consistently with practice timezone
  const formatDate = (dateString: string): string => {
    return formatDateInPracticeTimezone(
      dateString,
      practiceTimezone,
      practiceState
    );
  };

  // Helper function to format date object to YYYY-MM-DD string
  const formatDateToString = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Helper function to convert time string to minutes for comparison
  const convertTimeToMinutes = (timeString: string): number => {
    if (!timeString) return 0;

    const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)/);
    if (!match) return 0;

    let hour = parseInt(match[1]);
    const minute = parseInt(match[2]);
    const meridiem = match[3];

    // Convert to 24-hour format
    if (meridiem === 'PM' && hour !== 12) {
      hour += 12;
    } else if (meridiem === 'AM' && hour === 12) {
      hour = 0;
    }

    return hour * 60 + minute;
  };

  // Helper function to detect overnight closures
  const isOvernightClosure = (
    startTime: string,
    endTime: string
  ): boolean => {
    if (!startTime || !endTime) return false;

    // Parse time components
    const startMatch = startTime.match(/(\d+):(\d+)\s*(AM|PM)/);
    const endMatch = endTime.match(/(\d+):(\d+)\s*(AM|PM)/);

    if (!startMatch || !endMatch) return false;

    const startHour = parseInt(startMatch[1]);
    const endHour = parseInt(endMatch[1]);
    const startMeridiem = startMatch[3];
    const endMeridiem = endMatch[3];

    // Overnight: start time is PM, end time is AM
    const startIsPM = startMeridiem === 'PM' && startHour !== 12;
    const endIsAM = endMeridiem === 'AM' && endHour !== 12;

    return startIsPM && endIsAM;
  };

  // Manual refresh function for CRUD operations
  const refreshClosures = useCallback(async () => {
    hasFetched.current = false;
    const closuresData = await getPracticeClosures({
      practiceIds: [practiceId],
    });
    setClosures(closuresData);
    hasFetched.current = true;
  }, [practiceId]);

  // Fetch practice timezone information
  useEffect(() => {
    // Skip internal fetch when timezone/state are provided as props
    if (timezoneProp !== undefined) {
      setPracticeTimezone(timezoneProp ?? null);
      setPracticeState(stateProp ?? null);
      return;
    }
    // Existing fallback: fetch from API
    const fetchPracticeInfo = async () => {
      try {
        const { getPracticeById } = await import(
          '../../../services/practices/getPracticeById'
        );
        const { data } = await getPracticeById(practiceId);
        if (data?.practice) {
          setPracticeTimezone(data.practice.timezone);
          setPracticeState(data.practice.state);
        }
      } catch (error) {
        console.error('Error fetching practice timezone:', error);
        // Continue without timezone info - will fallback to local timezone
      }
    };
    if (practiceId) fetchPracticeInfo();
  }, [practiceId, timezoneProp, stateProp]);

  // Sync local closures state when parent prop transitions (e.g., null → [] after parent fetch completes)
  useEffect(() => {
    if (closuresProp !== undefined) {
      setClosures(closuresProp);
    }
  }, [closuresProp]);

  useEffect(() => {
    // Skip initial fetch when parent provides data (including null sentinel)
    if (closuresProp !== undefined) {
      hasFetched.current = true;
      return;
    }

    // Only fetch if we haven't already
    if (!hasFetched.current) {
      const fetchPracticeClosures = async () => {
        if (!isAuthenticated || !accessToken) {
          return;
        }

        setLoadingClosures(true);
        try {
          const closuresData = await getPracticeClosures({
            practiceIds: [practiceId],
          });
          setClosures(closuresData);
          hasFetched.current = true;
        } catch (error) {
          console.error('Error fetching closures:', error);
          setErrorClosures('Failed to load clinic closures.');
          // Don't set hasFetched to true on error to allow retry attempts
        } finally {
          setLoadingClosures(false);
        }
      };

      fetchPracticeClosures();
    }

    // Cleanup function to reset fetch flag on unmount
    return () => {
      hasFetched.current = false;
    };
  }, [practiceId, closuresProp, accessToken, isAuthenticated]);

  const openClosureModal = (closure?: PracticeClosure | null) => {
    if (closure) {
      // Edit existing closure
      setClosureFormData({
        id: closure.id,
        startDate: closure.startDate,
        endDate: closure.endDate,
        startTime: closure.startTime || '',
        endTime: closure.endTime || '',
        displayDate: closure.displayDate || '',
        note: closure.note || '',
      });
    } else {
      // Create new closure - set sensible defaults
      const today = new Date();

      setClosureFormData({
        id: '',
        startDate: formatDateToString(today), // Today as default start
        endDate: '',
        startTime: '',
        endTime: '',
        displayDate: '',
        note: '',
      });
    }
    setIsClosureModalOpen(true);
  };

  const resetForm = () => {
    setClosureFormData({
      id: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      displayDate: '',
      note: '',
    });
    setValidationError(null);
  };

  // Helper function to ensure dates are saved in correct format (YYYY-MM-DD)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';

    // Parse the date string in YYYY-MM-DD format explicitly to avoid timezone issues
    const [yearStr, monthStr, dayStr] = dateString.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (
      isNaN(year) ||
      isNaN(month) ||
      isNaN(day)
    ) {
      return '';
    }
    return dateString;
  };

  const handleSaveClosure = async () => {
    // Clear any previous validation errors
    setValidationError(null);

    // Validate required fields
    if (!closureFormData.startDate) {
      setValidationError('Start Date is required.');
      return;
    }

    // Validate dates using utility function
    const dateValidationResult = validateClosureDates(
      closureFormData.startDate,
      closureFormData.endDate,
      closureFormData.displayDate
    );

    if (!dateValidationResult.isValid) {
      setValidationError(
        dateValidationResult.errorMessage || 'Date validation failed.'
      );
      return;
    }

    setIsSaving(true);

    try {
      if ('id' in closureFormData && closureFormData.id) {
        // Update existing closure
        const updatePayload: PracticeClosureUpdatePayload = {
          id: closureFormData.id,
          startDate: formatDateForAPI(closureFormData.startDate),
          endDate: closureFormData.endDate
            ? formatDateForAPI(closureFormData.endDate)
            : '',
          startTime: closureFormData.startTime,
          endTime: closureFormData.endTime,
          displayDate: closureFormData.displayDate
            ? formatDateForAPI(closureFormData.displayDate)
            : '',
          note: closureFormData.note,
        };
        await updatePracticeClosure(updatePayload);
        analytics.trackEvent('update', 'closure');

        toast({
          title: 'Closure Updated',
          description:
            'Practice closure has been updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new closure
        const createPayload: PracticeClosureCreatePayload = {
          practiceId,
          startDate: formatDateForAPI(closureFormData.startDate),
          endDate: closureFormData.endDate
            ? formatDateForAPI(closureFormData.endDate)
            : '',
          startTime: closureFormData.startTime,
          endTime: closureFormData.endTime,
          displayDate: closureFormData.displayDate
            ? formatDateForAPI(closureFormData.displayDate)
            : '',
          note: closureFormData.note,
        };
        await createPracticeClosure(createPayload);
        analytics.trackEvent('create', 'closure');

        toast({
          title: 'Closure Created',
          description:
            'Practice closure has been created successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // Refresh closures and close modal
      await refreshClosures();
      setIsClosureModalOpen(false);
      resetForm();

      // Dispatch custom event to notify other components (e.g., PracticeStatusBox) to refresh clinic closures
      window.dispatchEvent(
        new CustomEvent('clinicClosureUpdated', {
          detail: { practiceId },
        })
      );
    } catch (error) {
      console.error('Error saving closure:', error);
      toast({
        title: 'Error',
        description:
          'Failed to save practice closure. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClosure = async (
    closureId: string
  ): Promise<void> => {
    try {
      await deletePracticeClosure([closureId]);
      analytics.trackEvent('delete', 'closure');

      // Fetch latest closures
      await refreshClosures();

      // Dispatch custom event to notify other components (e.g., PracticeStatusBox) to refresh clinic closures
      window.dispatchEvent(
        new CustomEvent('clinicClosureUpdated', {
          detail: { practiceId },
        })
      );

      toast({
        title: 'Success',
        description: 'Closure deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to delete closure:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete closure. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Sort closures by start date (most recent first)
  const sortedClosures = (closures || []).sort(
    (a, b) =>
      new Date(b.startDate).getTime() -
      new Date(a.startDate).getTime()
  );
  const [latestClosure, ...olderClosures] = sortedClosures;

  return (
    <Box
      padding="0.5rem 0.625rem"
      backgroundColor="yellow.100"
      borderRadius="0.5rem"
      width="100%"
      mt="0.25rem"
    >
      {loadingClosures ? (
        <Flex align="center" justify="center">
          <Spinner size="md" color="yellow.800" />
          <Text ml="1rem">Loading Clinic Closures...</Text>
        </Flex>
      ) : errorClosures ? (
        <Text color="red.500" textAlign="center">
          {errorClosures}
        </Text>
      ) : (
        <Stack spacing={4}>
          {latestClosure &&
            shouldShowCard(latestClosure.displayDate) && (
              <Box
                key={latestClosure.id}
                padding="1rem"
                backgroundColor="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="0.75rem"
                display="flex"
                flexDirection="column"
                gap="0.75rem"
                _hover={{
                  boxShadow: 'md',
                  borderColor: 'yellow.300',
                }}
                transition="all 0.2s ease-in-out"
              >
                {/* Header Row */}
                <Flex
                  justify="flex-start"
                  align="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <HStack spacing={3} align="center">
                    <WarningTwoIcon
                      color="yellow.600"
                      boxSize="1.25rem"
                      flexShrink={0}
                    />
                    <Text
                      fontSize="1rem"
                      fontWeight="600"
                      color="gray.800"
                    >
                      Practice Closure
                    </Text>
                  </HStack>

                  {/* Actions - Will wrap to next line if needed */}
                  {(canEditClosures() || isAdmin()) && (
                    <HStack
                      spacing={2}
                      minW="5rem"
                      justify="flex-end"
                      ml="auto"
                    >
                      <IconButton
                        aria-label="Edit Closure"
                        icon={<EditIcon />}
                        size="sm"
                        onClick={() =>
                          openClosureModal(latestClosure)
                        }
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'gray.100' }}
                      />
                      <IconButton
                        aria-label="Delete Closure"
                        icon={<DeleteIcon />}
                        size="sm"
                        onClick={() => {
                          setSelectedClosureId(latestClosure.id);
                          setIsDeleteModalOpen(true);
                        }}
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'gray.100' }}
                      />
                    </HStack>
                  )}
                </Flex>

                {/* Dates Row */}
                <HStack spacing={2} flexWrap="wrap">
                  <DateChip
                    label="Start"
                    value={
                      latestClosure.startTime
                        ? `${formatDate(
                            latestClosure.startDate
                          )} at ${latestClosure.startTime}`
                        : formatDate(latestClosure.startDate)
                    }
                    icon={CalendarIcon}
                  />
                  {latestClosure.endDate ? (
                    <DateChip
                      label="End"
                      value={
                        latestClosure.endTime
                          ? `${formatDate(
                              latestClosure.endDate
                            )} at ${latestClosure.endTime}`
                          : formatDate(latestClosure.endDate)
                      }
                      icon={CalendarIcon}
                    />
                  ) : (
                    <DateChip
                      label="End"
                      value="Indefinite"
                      icon={CalendarIcon}
                      variant="expired"
                    />
                  )}

                  {latestClosure.displayDate &&
                    (canViewDisplayDate() ||
                      canEditDisplayDate() ||
                      isAdmin()) && (
                      <DateChip
                        label="Display"
                        value={formatDate(latestClosure.displayDate)}
                        icon={TimeIcon}
                        variant={
                          new Date(latestClosure.displayDate) >
                          new Date()
                            ? 'scheduled'
                            : 'default'
                        }
                      />
                    )}
                </HStack>

                {/* Time Range Row - Only show for today's partial closures or overnight closures */}
                {latestClosure.startTime &&
                  latestClosure.endTime &&
                  (() => {
                    const startDate = new Date(
                      latestClosure.startDate
                    );
                    const endDate = latestClosure.endDate
                      ? new Date(latestClosure.endDate)
                      : startDate;
                    const today = new Date();
                    const isToday =
                      startDate.toDateString() ===
                        today.toDateString() &&
                      (latestClosure.endDate
                        ? endDate.toDateString() ===
                          today.toDateString()
                        : false);
                    const isOvernight = isOvernightClosure(
                      latestClosure.startTime,
                      latestClosure.endTime
                    );

                    // Only show time period for today's partial closures or overnight closures
                    if (isToday || isOvernight) {
                      return (
                        <VStack
                          align="flex-start"
                          spacing={2}
                          flex={1}
                        >
                          <HStack spacing={2} flexWrap="wrap">
                            {/* Time Period chip - only for today's partial closures */}
                            {isToday && (
                              <DateChip
                                label="Time Period"
                                value={`${latestClosure.startTime} - ${latestClosure.endTime}`}
                                icon={TimeIcon}
                                variant="scheduled"
                              />
                            )}

                            {/* Overnight indicator */}
                            {isOvernight && (
                              <HStack
                                spacing={1}
                                px={2}
                                py={1}
                                borderRadius="md"
                                border="1px solid"
                                bg="purple.50"
                                color="purple.700"
                                borderColor="purple.200"
                              >
                                <Text
                                  fontSize="0.85rem"
                                  fontWeight="600"
                                >
                                  🌙 Overnight
                                </Text>
                              </HStack>
                            )}
                          </HStack>

                          {/* Helper text for overnight closures */}
                          {isOvernight && (
                            <Text
                              fontSize="0.75rem"
                              color="purple.600"
                              fontStyle="italic"
                            >
                              Practice closed from{' '}
                              {latestClosure.startTime} on{' '}
                              {formatDate(latestClosure.startDate)}
                              until {latestClosure.endTime} on{' '}
                              {latestClosure.endDate
                                ? formatDate(latestClosure.endDate)
                                : 'indefinite date'}
                            </Text>
                          )}
                        </VStack>
                      );
                    }

                    // For multi-day closures with times, don't show time period
                    return null;
                  })()}

                {/* Note Preview */}
                <NotePreview
                  content={latestClosure.note || ''}
                  maxHeight="4rem"
                />
              </Box>
            )}

          {olderClosures.length > 0 && (
            <Collapse in={!isCollapsed}>
              {olderClosures
                .filter((closure) =>
                  shouldShowCard(closure.displayDate)
                )
                .map((closure) => (
                  <Box
                    key={closure.id}
                    padding="1rem"
                    backgroundColor="yellow.50"
                    border="1px solid"
                    borderColor="yellow.200"
                    borderRadius="0.75rem"
                    display="flex"
                    flexDirection="column"
                    gap="0.75rem"
                    mt="0.75rem"
                    _hover={{
                      boxShadow: 'md',
                      borderColor: 'yellow.300',
                    }}
                    transition="all 0.2s ease-in-out"
                  >
                    {/* Header Row */}
                    <Flex
                      justify="flex-start"
                      align="center"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <HStack spacing={3} align="center">
                        <WarningTwoIcon
                          color="yellow.600"
                          boxSize="1.25rem"
                          flexShrink={0}
                        />
                        <Text
                          fontSize="1rem"
                          fontWeight="600"
                          color="gray.800"
                        >
                          Practice Closure
                        </Text>
                      </HStack>

                      {/* Actions - Will wrap to next line if needed */}
                      {(canEditClosures() || isAdmin()) && (
                        <HStack
                          spacing={2}
                          minW="5rem"
                          justify="flex-end"
                          ml="auto"
                        >
                          <IconButton
                            aria-label="Edit Closure"
                            icon={<EditIcon />}
                            size="sm"
                            onClick={() => openClosureModal(closure)}
                            backgroundColor="white"
                            _hover={{ backgroundColor: 'gray.100' }}
                          />
                          <IconButton
                            aria-label="Delete Closure"
                            icon={<DeleteIcon />}
                            size="sm"
                            onClick={() => {
                              setSelectedClosureId(closure.id);
                              setIsDeleteModalOpen(true);
                            }}
                            backgroundColor="white"
                            _hover={{ backgroundColor: 'gray.100' }}
                          />
                        </HStack>
                      )}
                    </Flex>

                    {/* Dates Row */}
                    <HStack spacing={2} flexWrap="wrap">
                      <DateChip
                        label="Start"
                        value={
                          closure.startTime
                            ? `${formatDate(closure.startDate)} at ${
                                closure.startTime
                              }`
                            : formatDate(closure.startDate)
                        }
                        icon={CalendarIcon}
                      />
                      {closure.endDate ? (
                        <DateChip
                          label="End"
                          value={
                            closure.endTime
                              ? `${formatDate(closure.endDate)} at ${
                                  closure.endTime
                                }`
                              : formatDate(closure.endDate)
                          }
                          icon={CalendarIcon}
                        />
                      ) : (
                        <DateChip
                          label="End"
                          value="Indefinite"
                          icon={CalendarIcon}
                          variant="expired"
                        />
                      )}
                      {closure.displayDate &&
                        (canViewDisplayDate() ||
                          canEditDisplayDate() ||
                          isAdmin()) && (
                          <DateChip
                            label="Display"
                            value={formatDate(closure.displayDate)}
                            icon={TimeIcon}
                            variant={
                              new Date(closure.displayDate) >
                              new Date()
                                ? 'scheduled'
                                : 'default'
                            }
                          />
                        )}
                    </HStack>

                    {/* Time Range Row - Only show for today's partial closures or overnight closures */}
                    {closure.startTime &&
                      closure.endTime &&
                      (() => {
                        const startDate = new Date(closure.startDate);
                        const endDate = closure.endDate
                          ? new Date(closure.endDate)
                          : startDate;
                        const today = new Date();
                        const isToday =
                          startDate.toDateString() ===
                            today.toDateString() &&
                          (closure.endDate
                            ? endDate.toDateString() ===
                              today.toDateString()
                            : false);
                        const isOvernight = isOvernightClosure(
                          closure.startTime,
                          closure.endTime
                        );

                        // Only show time period for today's partial closures or overnight closures
                        if (isToday || isOvernight) {
                          return (
                            <VStack
                              align="flex-start"
                              spacing={2}
                              flex={1}
                            >
                              <HStack spacing={2} flexWrap="wrap">
                                {/* Time Period chip - only for today's partial closures */}
                                {isToday && (
                                  <DateChip
                                    label="Time Period"
                                    value={`${closure.startTime} - ${closure.endTime}`}
                                    icon={TimeIcon}
                                    variant="scheduled"
                                  />
                                )}

                                {/* Overnight indicator */}
                                {isOvernight && (
                                  <HStack
                                    spacing={1}
                                    px={2}
                                    py={1}
                                    borderRadius="md"
                                    border="1px solid"
                                    bg="purple.50"
                                    color="purple.700"
                                    borderColor="purple.200"
                                  >
                                    <Text
                                      fontSize="0.85rem"
                                      fontWeight="600"
                                    >
                                      🌙 Overnight
                                    </Text>
                                  </HStack>
                                )}
                              </HStack>

                              {/* Helper text for overnight closures */}
                              {isOvernight && (
                                <Text
                                  fontSize="0.75rem"
                                  color="purple.600"
                                  fontStyle="italic"
                                >
                                  Practice closed from{' '}
                                  {closure.startTime} on{' '}
                                  {formatDate(closure.startDate)}
                                  until {closure.endTime} on{' '}
                                  {closure.endDate
                                    ? formatDate(closure.endDate)
                                    : 'indefinite date'}
                                </Text>
                              )}
                            </VStack>
                          );
                        }

                        // For multi-day closures with times, don't show time period
                        return null;
                      })()}

                    {/* Note Preview */}
                    <NotePreview
                      content={closure.note || ''}
                      maxHeight="4rem"
                    />
                  </Box>
                ))}
            </Collapse>
          )}

          {/* Add Closure and Show More buttons - will wrap to next line if needed */}
          <Flex
            justify="flex-start"
            align="center"
            flexWrap="wrap"
            gap={2}
          >
            {/* Show "No Data" message when no closures */}
            {(!closures || closures.length === 0) && (
              <Text
                fontSize="0.875rem"
                color="gray.500"
                fontStyle="italic"
                textAlign="center"
                width="100%"
                py="1rem"
              >
                No Practice Closures
              </Text>
            )}

            {/* Show Add button only for users with permissions */}
            {(canEditClosures() || isAdmin()) && (
              <Button
                size="sm"
                leftIcon={<AddIcon />}
                onClick={() => openClosureModal(null)}
                backgroundColor="yellow.100"
                color="black"
                _hover={{ backgroundColor: 'yellow.200' }}
              >
                Add Practice Closure
              </Button>
            )}
            {olderClosures.filter((closure) =>
              shouldShowCard(closure.displayDate)
            ).length > 0 && (
              <Button
                size="sm"
                leftIcon={
                  isCollapsed ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronUpIcon />
                  )
                }
                onClick={() => setIsCollapsed(!isCollapsed)}
                backgroundColor="yellow.100"
                color="black"
                _hover={{ backgroundColor: 'yellow.200' }}
              >
                {isCollapsed
                  ? `Show ${
                      olderClosures.filter((closure) =>
                        shouldShowCard(closure.displayDate)
                      ).length
                    } more`
                  : 'Hide older closures'}
              </Button>
            )}
          </Flex>
        </Stack>
      )}

      <Modal
        isOpen={isClosureModalOpen}
        onClose={() => setIsClosureModalOpen(false)}
        size="6xl"
        isCentered
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px)"
        />
        <ModalContent
          maxW="90vw"
          maxH="90vh"
          mx="auto"
          borderRadius="xl"
          boxShadow="2xl"
        >
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="1rem"
            bg="yellow.50"
            borderTopRadius="xl"
          >
            <Text fontSize="1.25rem" fontWeight="600">
              Practice Closures
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p="2rem">
            <VStack spacing="1.5rem" align="stretch">
              <HStack align="flex-end" spacing="1rem">
                <Box flex="1">
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Start Date <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    When this closure begins. Can be in the past.
                  </Text>
                  <HStack spacing={2}>
                    <Input
                      type="date"
                      value={closureFormData.startDate}
                      onChange={(e) => {
                        setValidationError(null);
                        setClosureFormData({
                          ...closureFormData,
                          startDate: e.target.value,
                        });
                      }}
                      size="md"
                      borderRadius="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                      flex={1}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        setClosureFormData({
                          ...closureFormData,
                          startDate: formatDateToString(tomorrow),
                        });
                      }}
                      title="Set start date to tomorrow"
                    >
                      Tomorrow
                    </Button>
                  </HStack>
                </Box>

                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    End Date (Optional)
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    When this closure ends. Must be after start date.
                  </Text>
                  <HStack spacing={2}>
                    <Input
                      type="date"
                      value={closureFormData.endDate || ''}
                      onChange={(e) => {
                        setValidationError(null);
                        setClosureFormData({
                          ...closureFormData,
                          endDate: e.target.value || undefined,
                        });
                      }}
                      size="md"
                      borderRadius="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                      flex={1}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (closureFormData.startDate) {
                          const startDate = new Date(
                            closureFormData.startDate
                          );
                          const weekLater = new Date(startDate);
                          weekLater.setDate(weekLater.getDate() + 7);

                          setClosureFormData({
                            ...closureFormData,
                            endDate: formatDateToString(weekLater),
                          });
                        }
                      }}
                      isDisabled={!closureFormData.startDate}
                      title="Set end date to 1 week from start date"
                    >
                      +1 Week
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setClosureFormData({
                          ...closureFormData,
                          endDate: undefined,
                        });
                      }}
                      title="Clear end date (indefinite closure)"
                    >
                      Clear
                    </Button>
                  </HStack>
                </Box>
              </HStack>

              <HStack align="flex-end" spacing="1rem">
                <Box flex="1">
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Start Time (Optional)
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Leave empty if closure is for the entire day.
                  </Text>
                  <HStack spacing={2}>
                    <ReactSelect<TimeOption>
                      options={timeOptions}
                      value={timeOptions.find(
                        (option) =>
                          option.value === closureFormData.startTime
                      )}
                      onChange={(selectedOption) =>
                        setClosureFormData({
                          ...closureFormData,
                          startTime: selectedOption?.value || '',
                        })
                      }
                      isDisabled={!closureFormData.startDate}
                      placeholder="Select Start Time"
                      isSearchable={true}
                      isClearable={true}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: '40px',
                          minWidth: '100%',
                          width: '100%',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.375rem',
                          boxShadow: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            border: '1px solid #cbd5e0',
                          },
                          '&:focus-within': {
                            border: '1px solid #3182ce',
                            boxShadow: '0 0 0 1px #3182ce',
                          },
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          fontSize: '0.875rem',
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? '#3182ce'
                            : state.isFocused
                            ? '#e6f3ff'
                            : 'white',
                          color: state.isSelected
                            ? 'white'
                            : '#2d3748',
                          fontSize: '0.875rem',
                          padding: '0.5rem 0.75rem',
                          '&:hover': {
                            backgroundColor: state.isSelected
                              ? '#3182ce'
                              : '#e6f3ff',
                          },
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#2d3748',
                          fontWeight: '500',
                          fontSize: '0.875rem',
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#a0aec0',
                          fontSize: '0.875rem',
                        }),
                        input: (provided) => ({
                          ...provided,
                          fontSize: '0.875rem',
                        }),
                        indicatorSeparator: (provided) => ({
                          ...provided,
                          display: 'none',
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#a0aec0',
                          '&:hover': {
                            color: '#4a5568',
                          },
                        }),
                      }}
                    />
                  </HStack>
                </Box>
                <Box flex="1">
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    End Time (Optional)
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Leave empty if closure is for the entire day.
                  </Text>
                  <HStack spacing={2}>
                    <ReactSelect<TimeOption>
                      options={timeOptions}
                      value={timeOptions.find(
                        (option) =>
                          option.value === closureFormData.endTime
                      )}
                      onChange={(selectedOption) =>
                        setClosureFormData({
                          ...closureFormData,
                          endTime: selectedOption?.value || '',
                        })
                      }
                      isDisabled={!closureFormData.startDate}
                      placeholder="Select End Time"
                      isSearchable={true}
                      isClearable={true}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: '40px',
                          minWidth: '100%',
                          width: '100%',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.375rem',
                          boxShadow: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            border: '1px solid #cbd5e0',
                          },
                          '&:focus-within': {
                            border: '1px solid #3182ce',
                            boxShadow: '0 0 0 1px #3182ce',
                          },
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          fontSize: '0.875rem',
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? '#3182ce'
                            : state.isFocused
                            ? '#e6f3ff'
                            : 'white',
                          color: state.isSelected
                            ? 'white'
                            : '#2d3748',
                          fontSize: '0.875rem',
                          padding: '0.5rem 0.75rem',
                          '&:hover': {
                            backgroundColor: state.isSelected
                              ? '#3182ce'
                              : '#e6f3ff',
                          },
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#2d3748',
                          fontWeight: '500',
                          fontSize: '0.875rem',
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#a0aec0',
                          fontSize: '0.875rem',
                        }),
                        input: (provided) => ({
                          ...provided,
                          fontSize: '0.875rem',
                        }),
                        indicatorSeparator: (provided) => ({
                          ...provided,
                          display: 'none',
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#a0aec0',
                          '&:hover': {
                            color: '#4a5568',
                          },
                        }),
                      }}
                    />
                  </HStack>
                </Box>
              </HStack>

              {(canEditDisplayDate() || isAdmin()) && (
                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Display Date
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Optional: When to start showing this closure. Can
                    be before the start date. Leave empty to display
                    immediately.
                  </Text>
                  <HStack spacing={2}>
                    <Input
                      type="date"
                      value={closureFormData.displayDate || ''}
                      onChange={(e) => {
                        setValidationError(null);
                        setClosureFormData({
                          ...closureFormData,
                          displayDate: e.target.value || undefined,
                        });
                      }}
                      size="md"
                      borderRadius="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                      flex={1}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setClosureFormData({
                          ...closureFormData,
                          displayDate: undefined,
                        });
                      }}
                      title="Clear display date"
                    >
                      Clear
                    </Button>
                  </HStack>
                </Box>
              )}

              {(canEditDisplayDate() || isAdmin()) && (
                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Notes
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Optional: Additional information about this
                    closure.
                  </Text>
                  <Box position="relative">
                    <RichTextEditor
                      value={closureFormData.note || ''}
                      onChange={(value) => {
                        setValidationError(null);
                        setClosureFormData({
                          ...closureFormData,
                          note: value || undefined,
                        });
                      }}
                      placeholder="Optional: Enter closure notes..."
                      minHeight="8rem"
                      maxHeight="20rem"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setClosureFormData({
                          ...closureFormData,
                          note: undefined,
                        });
                      }}
                      title="Clear notes"
                      position="absolute"
                      top="0.5rem"
                      right="0.5rem"
                      zIndex={10}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              )}

              {validationError && (
                <Alert
                  status="error"
                  borderRadius="md"
                  fontSize="0.875rem"
                >
                  <AlertIcon />
                  {validationError}
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="1rem"
            bg="yellow.50"
            borderBottomRadius="xl"
          >
            <HStack spacing="1rem">
              <Button
                variant="outline"
                onClick={() => setIsClosureModalOpen(false)}
                size="md"
                borderRadius="md"
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={async () => {
                  // Validate end date is not in the past for NEW items only
                  const today = new Date();
                  today.setHours(0, 0, 0, 0); // Reset time to start of day

                  // Helper to parse YYYY-MM-DD into a local date for comparison
                  const parseLocalDate = (dateStr: string) => {
                    const [y, m, d] = dateStr.split('-').map(Number);
                    return new Date(y, m - 1, d);
                  };

                  // Only validate end date for NEW items (not when editing existing ones) and only if provided
                  if (
                    (!('id' in closureFormData) ||
                      !closureFormData.id ||
                      closureFormData.id === '') &&
                    closureFormData.endDate
                  ) {
                    const endDateObj = parseLocalDate(closureFormData.endDate);
                    // Start date can be in the past, but end date must be today or in the future
                    if (endDateObj < today) {
                      setValidationError(
                        `End Date (${closureFormData.endDate}) cannot be in the past for new closures.`
                      );
                      return;
                    }
                  }

                  // Centralized time validation rules
                  const startTimeVal =
                    closureFormData.startTime || '';
                  const endTimeVal = closureFormData.endTime || '';
                  const hasStartTime = startTimeVal !== '';
                  const hasEndTime = endTimeVal !== '';

                  if (hasStartTime && hasEndTime) {
                    // Check if this is a cross-midnight scenario
                    const startDate = parseLocalDate(closureFormData.startDate);
                    const endDate = closureFormData.endDate
                      ? parseLocalDate(closureFormData.endDate)
                      : startDate;
                    const isSameDay =
                      startDate.getTime() === endDate.getTime();

                    if (isSameDay) {
                      // Same day: end time must be after start time
                      // Convert times to comparable values for proper comparison
                      const startTimeMinutes =
                        convertTimeToMinutes(startTimeVal);
                      const endTimeMinutes =
                        convertTimeToMinutes(endTimeVal);

                      if (startTimeMinutes >= endTimeMinutes) {
                        setValidationError(
                          'End Time must be after Start Time for same-day closures.'
                        );
                        return;
                      }
                    } else {
                      // Different days: times can be in any order (cross-midnight scenarios)
                      // No validation needed for cross-midnight
                    }
                  } else if (hasStartTime !== hasEndTime) {
                    setValidationError(
                      'Provide both Start Time and End Time, or leave both empty for an all‑day closure.'
                    );
                    return;
                  }

                  setIsSaving(true);
                  try {
                    await handleSaveClosure();
                  } catch (error) {
                    toast({
                      title: 'Error',
                      description:
                        'Failed to save closure. Please try again.',
                      status: 'error',
                      duration: 3000,
                      isClosable: true,
                    });
                  } finally {
                    setIsSaving(false);
                  }
                }}
                isDisabled={!closureFormData.startDate || isSaving}
                size="md"
                borderRadius="md"
                isLoading={isSaving}
                loadingText="Saving..."
              >
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        size="md"
        isCentered
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px)"
        />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="1rem"
            bg="yellow.50"
            borderTopRadius="xl"
          >
            <Text fontSize="1.25rem" fontWeight="600">
              Confirm Deletion
            </Text>
          </ModalHeader>
          <ModalBody p="2rem">
            <Text fontSize="0.875rem" color="gray.700">
              Are you sure you want to delete this closure?
            </Text>
          </ModalBody>
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="1rem"
            bg="yellow.50"
            borderBottomRadius="xl"
          >
            <HStack spacing="1rem">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                size="md"
                borderRadius="md"
                isDisabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    if (selectedClosureId) {
                      await handleDeleteClosure(selectedClosureId);
                    }
                  } finally {
                    setIsDeleting(false);
                    setIsDeleteModalOpen(false);
                  }
                }}
                isLoading={isDeleting}
                loadingText="Deleting..."
                isDisabled={isDeleting}
                size="md"
                borderRadius="md"
              >
                Delete
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PracticeClosuresCard;
